<template>
  <!-- 论文稿次 提交弹窗 -->
  <a-modal
    :width="580"
    v-model:visible="props.show"
    title="上传论文"
    centered
    @cancel="cancel"
  >
    <div class="content_box">
      <a-alert
        message="提示：请按照要求将对应文件整理成压缩包上传，避免不通过论文审核"
        type="info"
        style="margin-bottom: 16px"
      />
      <a-form
        ref="formBox"
        :model="formData"
        :label-col="labelCol"
        :wrapper-col="wrapperCol"
      >
        <a-form-item
          v-if="isShow"
          label="论文题目"
          name="title"
          :rules="[{ required: true, message: '请输入论文题目' }]"
        >
          <a-textarea
            v-model:value="formData.title"
            placeholder="请输入论文题目"
            :auto-size="{ minRows: 2, maxRows: 4 }"
            :maxlength="40"
            showCount
          />
        </a-form-item>
        <a-form-item
          name="researchDirection"
          v-if="isShow"
          :rules="researchDirectionRules"
        >
          <template #label>
            <span>论文研究方向</span>
            <a-tooltip placement="top" :overlayStyle="{ maxWidth: '300px' }">
              <template #title>
                <span style="color: #fff">
                  最多可填2个方向（每个方向不超15字），用中文或英文分号；分隔
                </span>
              </template>
              <info-circle-outlined class="label-icon" />
            </a-tooltip>
          </template>
          <a-textarea
            v-model:value="formData.researchDirection"
            placeholder="请输入论文研究方向"
            :auto-size="{ minRows: 2, maxRows: 4 }"
            :maxlength="40"
            showCount
          />
        </a-form-item>
        <a-form-item name="outline" :rules="outlineRules">
          <template #label>
            <span>论文关键词</span>
            <a-tooltip placement="top" :overlayStyle="{ maxWidth: '300px' }">
              <template #title>
                <span style="color: #fff">
                  3-5个关键词，每个关键词之间用中文或英文分号；分隔开（每个关键词不超7个字）
                </span>
              </template>
              <info-circle-outlined class="label-icon" />
            </a-tooltip>
          </template>
          <a-textarea
            v-model:value="formData.outline"
            placeholder="请输入轮文关键词"
            :auto-size="{ minRows: 2, maxRows: 4 }"
            :maxlength="40"
            showCount
          />
        </a-form-item>
        <a-form-item
          name="fileList"
          label="上传文件"
          :rules="[{ required: true, message: '请输入轮文关键词' }]"
        >
          <div class="upload-box">
            <a-upload
              name="file"
              multiple
              accept=".rar, .zip"
              :show-upload-list="false"
              :before-upload="beforeUpload"
              :custom-request="onCustomRequest"
              @change="($event) => uploadChange($event)"
            >
              <a-button :loading="state.btnLoading">
                <upload-outlined></upload-outlined>上传文件
              </a-button>
              <span class="upload-tips">
                仅支持1个不超过5MB的rar、zip压缩包文件
              </span>
            </a-upload>
            <div
              v-for="(item, index) in formData.fileList"
              :key="index"
              class="file-list-box"
              @click="openItem(item)"
            >
              <span class="file-list-title">{{ item.name }}</span>
              <div v-if="!item.isUploadSuccess" class="progress">
                <a-progress
                  :percent="item.percent"
                  :status="item.percent < 100 ? 'active' : 'success'"
                />
              </div>
            </div>
          </div>
        </a-form-item>
      </a-form>
    </div>
    <template #footer>
      <div class="btn_box">
        <a-button style="margin-right: 5px" @click="cancel">取消</a-button>
        <a-button type="primary" @click="handleOk" :loading="state.loading">
          确定
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script setup>
import { ref, reactive, watch } from "vue";
import { message, Modal } from "ant-design-vue";

import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons-vue";
import { uploadFile } from "@/api/my_page/paper/index.js";
import { uploadPaper } from "@/api/common-paper.js";
import { sdkOssUpimage } from "@/api/common.js";
import { getGuId, extractBirthAndGenderFromFileName } from "@/utils/function";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => {
      return {};
    },
  },
  type: {
    type: String,
    default: "",
  },
  isShow: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(["update:show", "updateList"]);

let state = reactive({
  loading: false,
  btnLoading: false,
  title: "论文提交",
});
const formBox = ref(null);
const formData = reactive({
  outline: "",
  fileUrl: "",
  fileList: [], // 文件列表
});
const labelCol = {
  span: 6,
};
const wrapperCol = {
  span: 18,
};

watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      let obj = { 0: "答辩", 1: "一稿", 2: "二稿", 3: "三稿" };
      let data = props.data;
      state.title = obj[data.submissionNum] + "论文提交";
      if (props.type == "common-paper") {
        formData.fileList = [
          {
            name: data.defenseFilename,
            url: data.defenseFile,
            isUploadSuccess: true,
            percent: 100,
          },
        ];
        formData.researchDirection = data.defenseResearchDirection;
        formData.outline = data.defenseKeyword;
        formData.title = data.defenseTitle;
      }
    } else {
      state.fileList = [];
      formBox.value.resetFields();
    }
  },
  { deep: true }
);

const researchDirectionRules = [
  {
    required: true,
    validator: (rule, value) => {
      if (!value || !value.trim()) {
        return Promise.reject("请输入论文研究方向");
      }

      const directions = value.split(/;|；/).map((dir) => dir.trim());

      if (directions.length === 0) {
        return Promise.reject("请输入有效的论文研究方向");
      }

      if (directions.length > 2) {
        return Promise.reject("最多可填2个方向");
      }

      for (const dir of directions) {
        if (dir.length > 15) {
          return Promise.reject("每个方向不超过15字");
        }
      }
      return Promise.resolve();
    },
  },
];

const outlineRules = [
  {
    required: true,
    validator: (rule, value) => {
      if (!value || !value.trim()) {
        return Promise.reject("请输入论文关键词");
      }

      // 分割并去除前后空白字符，同时过滤掉空字符串
      const keywords = value
        .split(/;|；/)
        .map((kw) => kw.trim())
        .filter((kw) => kw);

      if (keywords.length === 0) {
        return Promise.reject("请输入有效的论文关键词");
      }

      if (keywords.length < 3 || keywords.length > 5) {
        return Promise.reject("请确保输入3到5个关键词");
      }

      for (const kw of keywords) {
        if (kw.length > 7) {
          return Promise.reject("每个关键词不超过7个字");
        }
      }

      return Promise.resolve();
    },
  },
];

/**
 * 附件上传前的回调
 **/
const beforeUpload = (file) => {
  const allowExtentions = ["rar", "zip"];
  const fileNameArray = file.name.includes(".")
    ? file.name.split(".")
    : ["nonono"];
  const fileType = fileNameArray[fileNameArray.length - 1].toLowerCase();
  const isMaxSize = file.size / 1024 / 1024 < 5; // 修改为5MB
  const isFileType = allowExtentions.includes(fileType);

  if (!isMaxSize) {
    message.error("文件大小不得超过5MB!");
  }
  if (!isFileType) {
    message.error("只能上传RAR或ZIP格式文件!");
  }
  return isFileType && isMaxSize;
};

//打开附件
const openItem = (item) => {
  Modal.confirm({
    title: "下载提示",
    content: `是否下载\n${item.name}`,
    onOk: () => {
      const useragent = navigator.userAgent;
      if (useragent.match(/MicroMessenger/i) != "MicroMessenger") {
        // 非微信，直接打开
        window.open(item.url, "_blank");
      }
    },
  });
};

// 上传回调
const onCustomRequest = async (e) => {
  const { file } = e;
  state.btnLoading = true;
  formData.fileList = [];
  const nowDateTs = new Date().getTime(); // 当前时间时间戳
  formData.fileList.push({
    id: nowDateTs,
    name: file.name,
    isUploadSuccess: false,
    percent: 0,
  });
  const uploadIndex = formData.fileList.findIndex(
    (item) => item.id === nowDateTs
  );
  let index = 0;
  const fakeUploadLoading = () => {
    setTimeout(() => {
      if (index >= 3) return;
      index++;
      formData.fileList[uploadIndex].percent = 33 * index;
      fakeUploadLoading();
    }, 1000);
  };
  fakeUploadLoading();
  let client = await sdkOssUpimage();

  const fileName = `${client.options.env}/student/${
    extractBirthAndGenderFromFileName(file).nameWithoutExtension
  }_${getGuId()}.${extractBirthAndGenderFromFileName(file).fileExtension}`;

  client.put(fileName, file).then((result) => {
    formData.fileList[uploadIndex].url = result.url;
    formData.fileList[uploadIndex].percent = 100;
    index = 3;
    setTimeout(() => {
      formData.fileList[uploadIndex].isUploadSuccess = true;
    }, 1000);
    if (formData.fileList.length > 1) {
      message.error("最多上传1个文件！");
      formData.fileList = formData.fileList.splice(0, 1);
    }
    formBox.value.validate("fileList");
    state.btnLoading = false;
  });
};

/**
 * 上传状态改变
 **/
const uploadChange = (info) => {
  const { file } = info;

  if (file.status === "uploading") {
    return;
  }
  if (file.status === "error") {
    message.error("上传失败");
  }
};

const setDraftParams = (stage, params) => {
  const { title, researchDirection, outline, fileList } = formData;

  const file = fileList[0];
  const prefix = `draft${stage}`;

  if (props.isShow) {
    params[`${prefix}Title`] = title;
    params[`${prefix}ResearchDirection`] = researchDirection;
  }
  params[`${prefix}Keyword`] = outline;
  params[`${prefix}Filename`] = file.name;
  params[`${prefix}File`] = file.url;
};

// 点击确定
function handleOk() {
  formBox.value.validate().then(() => {
    Modal.confirm({
      title: "提示",
      content: "确定后无法修改，请确认后再进行提交",
      onOk: async () => {
        const { title, researchDirection, outline, fileList } = formData;
        let params = {};
        state.loading = true;
        try {
          if (props.type == "common-paper") {
            //一稿
            if (props.data.studentPaperStage === "FIRST_DRAFT") {
              setDraftParams(1, params);
            }
            //二稿
            if (props.data.studentPaperStage === "SECOND_DRAFT") {
              setDraftParams(2, params);
            }
            //三稿
            if (props.data.studentPaperStage === "THIRD_DRAFT") {
              setDraftParams(3, params);
            }
            //论文答辩
            if (props.data.studentPaperStage === "DEFENSE") {
              params.defenseTitle = title;
              params.defenseResearchDirection = researchDirection;
              params.defenseKeyword = outline;
              params.defenseFilename = fileList[0].name;
              params.defenseFile = fileList[0].url;
            }
            params.studentPaperId = props.data.studentPaperId;
            await uploadPaper(params);
          } else {
            params = {
              paperStudentMajorId: props.data.id,
              type: props.data.type,
              title,
              researchDirection,
              outline,
              fileUrl: fileList[0].url,
            };
            await uploadFile(params);
          }
          state.loading = false;
          cancel();
          updateList();
        } catch (e) {
          console.log(e);
          state.loading = false;
        }
      },
    });
  });
}

// 取消
function cancel() {
  emit("update:show", false);
}

function updateList() {
  emit("updateList");
}
</script>

<style lang="less" scoped>
.content_box {
  width: 100%;
  .btn_box {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .s1 {
    padding: 1px 2px;
    color: #1963f7;
    cursor: pointer;
  }
}

.label-icon {
  color: rgba(0, 0, 0, 0.65);
  margin: 0 5px;
}

.file-list-box {
  margin-top: 16px;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
}
</style>
